import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    TabsPartContentProps,
    TabsListContextType,
    TabsContextType,
    TabsPartKind
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const TABS_PART_NAMES: Record<TabsPartKind, string> = {
    content: "Content",
    trigger: "Trigger",
    list: "List",
    root: "Root"
};

export const TabsContext = createContext<TabsContextType | undefined>(undefined);

export const TabsListContext = createContext<TabsListContextType | undefined>(undefined);

export const useTabsContext = ({
    part
}: {
    part: TabsPartKind;
}): TabsContextType | undefined => {
    const context = useContext(TabsContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Tabs.${TABS_PART_NAMES[part]}: must be rendered inside <Tabs>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withTabsContext = <P extends object>(
    part: Exclude<TabsPartKind, "root">,
    Content: ComponentType<TabsPartContentProps<P>>
): FC<P> & {
    tabsPart: TabsPartKind;
} => {
    const TabsPartBase: FC<P> = (props) => {
        const context = useTabsContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    TabsPartBase.displayName = `Tabs.${TABS_PART_NAMES[part]}`;

    return Object.assign(TabsPartBase, {
        tabsPart: part
    });
};
